/* eslint-disable no-console */
import 'intersection-observer';
import { getCookie } from '@utiljs/cookie';
import { getEnv } from '@utils/getEnv';

/* jscpd:ignore-start */
const defaultTags = {
    cityid: getCookie('cityId'),
    acctId: getCookie('acctId'),
    wmPoiId: getCookie('wmPoiId'),
};

const reportPV = (cid: string) => {
    const valLab = {
        poi_id: defaultTags.wmPoiId, //eslint-disable-line
        custom: { _page_env: getEnv(), _asset_version: window.STATIC_ASSET_VERSION }, //eslint-disable-line
    };
    try {
        window.LXAnalytics('pageView', valLab, {}, cid);
    } catch (e) {
        console.log('LX PV异常：', e); // eslint-disable-line
    }
};

type ErrorInfo = {
    /** 错误名称(二级分类) */
    name: string;
    /** 错误内容 */
    message: string;
    /** 一级分类，默认为 “jsError” */
    category?: string;
    /** 其它扩展信息对象 */
    tags?: { [index: string]: string | number };
    /** 错误级别，默认 error */
    level?: 'ERROR' | 'WARN' | 'INFO';
};
/* jscpd:ignore-end */

type PhfExposureConfig = {
    owlParams?: Owl.IStartOptions;
    lxPvKey?: string;
    time?: number;
};

class PhfExposure {
    public static _instance: PhfExposure;

    private _observer: IntersectionObserver | null = null;

    private _owlInstance: typeof Owl | null = null;

    // private time: number = 0;

    private owlParams: Owl.IStartOptions;

    constructor(opts: PhfExposureConfig) {
        // this.time = opts.time ?? 0;
        this.owlParams = opts.owlParams!;
        this.init();
    }

    public static getInstance = (opts?: PhfExposureConfig) => {
        const defaultOpts = {
            owlParams: {
                devMode: !(window as any).__isProd,
                project: 'com.sankuai.waimaiepc.bargain.pcorder',
                pageUrl: window.location.hash,
                webVersion: window.STATIC_ASSET_VERSION,
            },
        };
        if (!PhfExposure._instance) {
            PhfExposure._instance = new PhfExposure({ ...defaultOpts, ...(opts || {}) });
        }

        return PhfExposure._instance;
    };

    init() {
        // 插件 Owl 初始化
        if (window.Owl && window.Owl.OWL) {
            this._owlInstance = new window.Owl.OWL(this.owlParams);
        }
        // 曝光监听初始化
        this._observer = new IntersectionObserver(entries => {
            entries.forEach(item => {
                if (item.isIntersecting && this._observer && this._owlInstance) {
                    try {
                        this.reportPhfCardPV();
                    } catch (e) {
                        this.reportPhfCardError({
                            name: '拼好饭卡片 PV 上报失败',
                            message: `${e && e.stack}`,
                        });
                    } finally {
                        // 上报一次就销毁，如果异常也销毁
                        this._observer.unobserve(item.target);
                    }
                }
            });
        });
    }

    // api 业务监控
    reportApi(api: Owl.IApiOpts) {
        if (this._owlInstance && this._owlInstance.addApi) {
            this._owlInstance.addApi(api);
        }
    }

    // 原始上报方法
    reportError(errObject: Owl.IError, opts?: Owl.IOpts) {
        if (this._owlInstance && this._owlInstance.addError) {
            this._owlInstance.addError(errObject, opts);
            console.error(errObject);
        }
    }

    /**
     * 拼好饭卡片 pv 上报
     */
    reportPhfCardPV() {
        if (this._owlInstance && this._owlInstance.resetPv) {
            this._owlInstance.resetPv({
                pageUrl: window.location.hash,
            });
        }

        reportPV('c_waimai_e_trw1lqfc');
    }

    /**
     * 拼好饭 Owl 异常上报
     * @param param0
     */
    reportPhfCardError({
        name,
        message,
        category = 'jsError',
        tags = {},
        level = 'ERROR',
    }: ErrorInfo) {
        try {
            if (this._owlInstance && this._owlInstance.addError) {
                this._owlInstance.addError(
                    {
                        name,
                        msg: message,
                    },
                    {
                        level: Owl.errorModel.LEVEL[level],
                        combo: false,
                        tags: { ...defaultTags, ...tags },
                        category,
                    },
                );
            }
        } catch (e) {
            console.error('拼好饭模块 ERROR 上报失败');
        }
    }

    /**
     * 上报自定义指标
     *
     * _📢  OWL SDK中上报自定义指标强制为延迟上报(默认1500ms)，本工程OWL初始化时已设置成 10ms。_
     *
     * _📢  如需在页面跳转前上报自定义指标，请务必给跳转操作增加延时，否则可能导致上报数据不完整。_
     *
     * 附[Owl手动上报自定义指标文档](https://km.sankuai.com/custom/onecloud/page/253147798#id-%E6%89%8B%E5%8A%A8%E4%B8%8A%E6%8A%A5%E8%87%AA%E5%AE%9A%E4%B9%89%E6%8C%87%E6%A0%87)
     *
     * @param metricsKey 指标名称（需提前在Raptor系统配置）
     * @param metricsValue 指标值
     * @param tags 扩展维度（需提前在Raptor系统配置）
     */
    reportPhfCardCustomMetrics = (
        metricsKey: string,
        metricsValue: string | number,
        tags?: { [key: string]: string | number },
        ExtraData?: { [key: string]: string | number },
    ) => {
        try {
            if (this._owlInstance) {
                const metricsInstance = this._owlInstance.MetricManager();
                if (metricsInstance) {
                    const _tags = {
                        ...tags,
                        ...window?.POI_BASIC_INFO,
                    };

                    if (_tags) {
                        metricsInstance.setTags(_tags);
                    }

                    if (ExtraData) {
                        metricsInstance.setExtraData(ExtraData);
                    }
                    metricsInstance.setMetric(metricsKey, metricsValue);
                }
            }
        } catch (e) {
            console.log('自定义指标上报失败');
        }
    };

    add(ele: Element) {
        if (this._observer) {
            this._observer.observe(ele);
        }
    }

    // 取消监听
    destroy(ele: Element) {
        if (this._observer) {
            this._observer.unobserve(ele);
        }
    }
}

export default PhfExposure;
